// Lyra Main App Component

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';

import { SkeletonDashboard } from './components/ui/Skeleton';
import LandingPage from './components/landing/LandingPage';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import WelcomeDashboard from './components/dashboard/WelcomeDashboard';
import TeachingPage from './components/teaching/TeachingPage';
import TopicSelectorPage from './components/session/TopicSelectorPage';
import SessionPage from './components/session/SessionPage';
import SessionSummaryPage from './components/session/SessionSummaryPage';
import LibraryPage from './components/topics/LibraryPage';
import OnboardingPage from './components/onboarding/OnboardingPage';
import SettingsPage from './components/settings/SettingsPage';
import JourneyDetailPage from './components/journeys/JourneyDetailPage';
import CreateJourneyPage from './components/journeys/CreateJourneyPage';
import { PrivacyPolicy, TermsAndConditions, CookiePolicy, GDPR } from './components/legal';
import JourneyPlannerPage from './components/library/JourneyPlannerPage';
import WrittenPrepPage from './components/preparation/WrittenPrepPage';
import TopicWorkspace from './components/workspace/TopicWorkspace';

const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-neutral-medium mb-4">404</h1>
      <p className="text-neutral-dark dark:text-white mb-4">Siden blev ikke fundet</p>
      <a href="/" className="text-primary-600 hover:underline">
        Gå til forsiden
      </a>
    </div>
  </div>
);

// Protected Route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  skipOnboardingCheck?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, skipOnboardingCheck = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        <SkeletonDashboard />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if not completed (unless we're already on onboarding page)
  if (!skipOnboardingCheck && !user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects to dashboard if authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

// Auth page loading skeleton
const AuthLoadingSkeleton: React.FC = () => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
    <div className="w-full max-w-md p-8 space-y-6">
      {/* Logo skeleton */}
      <div className="flex justify-center">
        <div className="w-24 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
      {/* Title skeleton */}
      <div className="space-y-2 text-center">
        <div className="w-48 h-7 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mx-auto" />
        <div className="w-64 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mx-auto" />
      </div>
      {/* Form skeleton */}
      <div className="space-y-4">
        <div className="w-full h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="w-full h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="w-full h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>
);

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthLoadingSkeleton />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Landing Route wrapper (shows landing page for non-authenticated, redirects to dashboard for authenticated)
const LandingRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthLoadingSkeleton />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
};

// Main App Router
const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <WelcomeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <TeachingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teaching"
        element={<Navigate to="/sessions" replace />}
      />
      {/* Library */}
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library/:journeyId"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library/journey/:journeyId"
        element={
          <ProtectedRoute>
            <JourneyPlannerPage />
          </ProtectedRoute>
        }
      />
      {/* Written Preparation */}
      <Route
        path="/preparation"
        element={
          <ProtectedRoute>
            <WrittenPrepPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preparation/:journeyId"
        element={
          <ProtectedRoute>
            <WrittenPrepPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preparation/:journeyId/:topicId"
        element={
          <ProtectedRoute>
            <WrittenPrepPage />
          </ProtectedRoute>
        }
      />
      {/* Workspace */}
      <Route
        path="/workspace/:journeyId/:topicId"
        element={
          <ProtectedRoute>
            <TopicWorkspace />
          </ProtectedRoute>
        }
      />
      {/* Legacy redirects */}
      <Route path="/topics" element={<Navigate to="/library" replace />} />
      <Route path="/archive" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journeys/new"
        element={
          <ProtectedRoute>
            <CreateJourneyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journeys/:journeyId"
        element={
          <ProtectedRoute>
            <JourneyDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/:sessionId"
        element={
          <ProtectedRoute>
            <SessionPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy briefing route redirects to session */}
      <Route path="/session/briefing/:sessionId" element={<Navigate to="/teaching" replace />} />
      <Route
        path="/session/summary/:sessionId"
        element={
          <ProtectedRoute>
            <SessionSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/setup/:mode"
        element={
          <ProtectedRoute>
            <TopicSelectorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<LandingRoute />} />

      {/* Legal */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/gdpr" element={<GDPR />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <div className="font-sans antialiased">
              <AppRouter />
            </div>
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
